/**
 * Forecasts API Service
 */

import api from "../client";
import { ENDPOINTS } from "../endpoints";
import { apiClient } from "../client";
import type {
  GeographicHierarchy,
  GeographicNode,
  DailyForecast,
  ForecastVariable,
  GeoJSONFeatureCollection,
  ListGeographicHierarchiesParams,
  ListGeographicNodesParams,
  ListDailyForecastsParams,
  ForecastDataParams,
  ForecastDataResponse,
  ForecastDataItem,
  ForecastExportRequest,
} from "../types";

export const forecastsApi = {
  // Storm Impact Models

  // Geographic Hierarchies
  geographicHierarchies: {
    list: (params?: ListGeographicHierarchiesParams) =>
      api.get<GeographicHierarchy[]>(ENDPOINTS.GEOGRAPHIC_HIERARCHIES, {
        params,
      }),

    getById: (id: string) =>
      api.get<GeographicHierarchy>(ENDPOINTS.GEOGRAPHIC_HIERARCHY(id)),
  },

  // Geographic Nodes
  geographicNodes: {
    list: (params?: ListGeographicNodesParams) =>
      api.get<GeographicNode[]>(ENDPOINTS.GEOGRAPHIC_NODES, { params }),

    getById: (id: string) =>
      api.get<GeographicNode>(ENDPOINTS.GEOGRAPHIC_NODE(id)),
  },

  // Forecast Variables
  forecastVariables: {
    list: () => api.get<ForecastVariable[]>(ENDPOINTS.FORECAST_VARIABLES),

    getById: (id: string) =>
      api.get<ForecastVariable>(ENDPOINTS.FORECAST_VARIABLE(id)),
  },

  // Daily Forecasts
  dailyForecasts: {
    list: (params: ListDailyForecastsParams) =>
      api.get<DailyForecast[]>(ENDPOINTS.DAILY_FORECASTS, { params }),

    getById: (id: string) =>
      api.get<DailyForecast>(ENDPOINTS.DAILY_FORECAST(id)),
  },

  // GeoJSON
  geojson: {
    get: (organizationId: string) =>
      api.get<GeoJSONFeatureCollection>(ENDPOINTS.GEOJSON(organizationId)),
  },

  /**
   * Fetch forecast data for given parameters 
   */
  forecasts: {
    list: async (params: ForecastDataParams): Promise<ForecastDataResponse> => {
      const response = await api.get<ForecastDataResponse>(
        ENDPOINTS.FORECASTS,
        {
          params: {
            modelId: params.modelId,
            weatherModelId: params.weatherModelId,
            geographicNodeIds: params.geographicNodeIds,
            min_date: params.min_date,
            max_date: params.max_date,
            run: params.run,
            limit: params.limit || 200,
            ...(params.aggregationNodeId && {
              aggregationNodeId: params.aggregationNodeId,
            }),
          },
        },
      );
      return {
        items: response.items || [],
        count: response.count || 0,
        aggregation: response.aggregation || null,
      };
    },
  },
  /**
   * Export forecast data as a downloadable file (CSV or Excel).
   * Returns a Blob that can be used to trigger a file download.
   */
  exportForecasts: async (request: ForecastExportRequest): Promise<Blob> => {
    const response = await apiClient.post(
      ENDPOINTS.EXPORT_FORECASTS,
      request,
      {
        responseType: "blob",
      },
    );
    return response.data;
  },
};

export default forecastsApi;
